import React, { useEffect, useState } from 'react';
import { AttendeesListItem } from './_attendees-list-item/attendees-list-item';
import { AttendeeListViewModel } from './attendees-list.viewmodel';
import * as mobxReact from 'mobx-react';
import './attendees-list.scss';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import classNames from 'classnames';
import { v4 as uuid } from 'uuid';
import { showColumn, showedAttendeeColumnList } from '../../../../infrastructure/utils/show-column';
import MultiSelectionDropdown from '../../../global/multi-selection-dropdown/multi-selection-dropdown';
import { SelectLeftColumnIcon } from '../../../global/icons/icons';
import { Permission } from '../../../../api/roles.dao';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { SearchInput } from '../../../global/search-input/search-input';
import OptionsButton from '../../../../components/global/options-button/options-button';
import { Paging } from '../../../global/_paging/paging-container';
import { ManageEventNavigation } from '../shared/navigation/navigation';
import { ITheadSort, ListLayout } from '../../../global/tables/table-list-layout';
import TableLoadingRow from '../../../global/tables/table-loading-row';
import DefaultButton from '../../../global/default-button/default-button';
import { IAttendeeListItemDTO, TGetAttendeesSortKeys } from '../../../../api/attendees.dao';
import { MultiSelectOption } from '../../../global/multi-select-list/_multi-select-option';
import { AttendeeUploadPopover } from './_attendee-upload-popover/attendee-upload-popover';
import { OpenFrom } from '../../../../tracking-service/tracking-values';
import { EMPTY_SEARCH_RESULT, MORE_BUTTON_TEXT } from '../../../../constants/common-labels.constants';

const ID_FOR_REGISTRATION_STATUS = uuid();
const ID_FOR_COLUMN_SELECTOR = uuid();
const ID_FOR_ATTENDANCE_METHODS = uuid();

const AttendeesListPresentation = injectIntl(mobxReact.observer((props: { vm: AttendeeListViewModel; intl: IntlShape; resetSearchQuery: () => void }) => {
    const {
        vm,
        resetSearchQuery,
        intl
    } = props;

    const isAllAttendeesSelected = (attendeesIDs: number[]): boolean => {
        return attendeesIDs.length && attendeesIDs.every(attendeesId => vm.attendeeSelectedIds.includes(attendeesId));
    };

    const isAttendeeSelected = (attendeeId: number): boolean => {
        return vm.attendeeSelectedIds.includes(attendeeId);
    };

    const registrationStatusLabel = intl.formatMessage({
        id: 'oe.common.registrationStatus',
        defaultMessage: 'Registration Status',
        description: 'Label text'
    });

    const attendanceMethodLabel = intl.formatMessage({
        id: 'oe.attendee.attendanceMethod',
        defaultMessage: 'Attendance Method',
        description: 'Label text'
    });

    const bulkAttendeeSelection = intl.formatMessage({
        id: 'oe.common.bulkAttendeeSelection',
        defaultMessage: 'Bulk Select',
        description: 'Label text'
    });

    const isLoading = vm.isFetchingAttendees();
    const isEmpty = !vm.getAttendees()?.length && !isLoading;
    const isEditable = hasPermission(Permission.UPDATE_ATTENDEE);

    const list: ITheadSort<TGetAttendeesSortKeys>[] = [
        { id: 'email', title: 'Email', sortField: 'email', byDefault: true },
        { id: 'organization', title: 'Organization', sortField: 'organization.name', byDefault: true },
        { id: 'organizationType', title: 'Organization Type', sortField: 'organization.organizationType', byDefault: true },
        {
            id: 'attendanceMethod',
            alternativeTitle: attendanceMethodLabel,
            title:  <MultiSelectionDropdown
                entityName={attendanceMethodLabel}
                withSearch={false}
                onFetchItems={vm.getAttendanceMethods}
                onApply={attendanceMethods => vm.handleChangeAttendanceMethodFilter(attendanceMethods)}
                savedItems={vm.selectedFilterAttendanceMethods.length ? vm.selectedFilterAttendanceMethods.split(',') : []}
                key={ID_FOR_ATTENDANCE_METHODS}
            />,
            sortField: null,
            byDefault: false
        },
        { id: 'title', title: 'Title', sortField: 'title', byDefault: true },
        { id: 'phoneNumber', title: 'Phone number', sortField: 'phoneNumber', byDefault: true },
        { id: 'salutation', title: 'Salutation', sortField: 'salutation', byDefault: true },
        { id: 'assistantEmail', title: 'Assistant Email', sortField: 'assistantEmail', byDefault: true },
        { id: 'attendeeId', title: 'ID', sortField: 'attendeeId', byDefault: true },
        { id: 'accessCode', title: 'Access Code', sortField: 'accessCode', byDefault: true },
        { id: 'registrationDate', title: 'Registration Date', sortField: 'registrationDate', byDefault: true },
        {
            id: 'registrationStatus',
            alternativeTitle: registrationStatusLabel,
            title: (
                <MultiSelectionDropdown
                    entityName={registrationStatusLabel}
                    withSearch={false}
                    onFetchItems={vm.getRegistrationStatuses}
                    onApply={registrationStatuses => vm.handleChangeRegistrationStatusFilter(registrationStatuses)}
                    savedItems={vm.selectedFilterStatuses.length ? vm.selectedFilterStatuses.split(',') : []}
                    key={ID_FOR_REGISTRATION_STATUS}
                />
            ),
            sortField: null,
            byDefault: false
        },
        { id: 'eventSubscriptionDate', title: 'Subscribed to Event', sortField: 'eventSubscriptionDate', byDefault: false },
        {
            id: 'bulkAttendeeSelection',
            alternativeTitle: bulkAttendeeSelection,
            title: (
                <MultiSelectOption
                    onClick={() => vm.handleAllAttendeeSelect(
                        vm.getAttendees().map(el => el.attendeeId),
                        isAllAttendeesSelected(vm.getAttendees().map(el => el.attendeeId))
                    )}
                    isSelected={!isLoading && (isAllAttendeesSelected(vm.getAttendees().map(el => el.attendeeId)))}
                />
            ),
            sortField: null,
            byDefault: false,
            columnPermission: Permission.UPDATE_ATTENDEE
        },
        ...vm.customFields?.map(field => ({ id: `${field.customFieldId}`, title: field.label, byDefault: false, sortField: undefined }))];

    const storedAttendeeColumnList: string = localStorage.getItem(showedAttendeeColumnList);
    const savedItems: string[] = storedAttendeeColumnList?.split(',') || list.filter(el => el.byDefault).map(el => el.id);
    const resetColumnSelector = (): void => {
        localStorage.setItem(showedAttendeeColumnList, list.filter(el => el.byDefault).map(el => el.id).join());
        setColumnFilter(list.filter(el => el.byDefault).join());
        setColumnSelectorTouched(false);
    };
    // Check this functionality
    // eslint-disable-next-line no-unused-vars
    const [columnFilter, setColumnFilter] = useState<string>('');
    const [isColumnSelectorTouched, setColumnSelectorTouched] = useState<boolean>(false);

    const isResetButtonDisabled: boolean = isColumnSelectorTouched || list.filter(el => el.byDefault).map(el => el.id).join() !== storedAttendeeColumnList;

    const theads: ITheadSort<TGetAttendeesSortKeys>[] = [
        {
            title: intl.formatMessage({
                id: 'oe.common.name',
                defaultMessage: 'Name',
                description: 'Label text'
            }),
            sortField: 'lastName',
        },
        ...list.filter(f => showColumn(f.id) && (f.columnPermission ? hasPermission(f.columnPermission) : true)).map(field => ({
            title: field.title,
            sortField: field.sortField
        })),
        {
            title: (
                <MultiSelectionDropdown
                    entityName=''
                    noItemsPlaceholder={<></>}
                    onApply={columns => {
                        localStorage.setItem(showedAttendeeColumnList, columns);
                        setColumnFilter(columns);
                        setColumnSelectorTouched(false);
                        if (!columns.includes('registrationStatus')) vm.handleChangeRegistrationStatusFilter('');
                        if (!columns.includes('attendanceMethod')) vm.handleChangeAttendanceMethodFilter('');
                        if (!columns.includes('bulkAttendeeSelection')) vm.clearAttendeeSelectedIds();
                    }}
                    withSearch={false}
                    withIcon={<SelectLeftColumnIcon />}
                    onFetchItems={() => new Promise(resolve => resolve(list
                        .filter(el => (el.columnPermission ? hasPermission(el.columnPermission) : true))
                        .map(el => {
                            return { id: el.id, label: el.alternativeTitle ?? (el.title as string) };
                        })
                    ))}
                    savedItems={savedItems}
                    customReset={resetColumnSelector}
                    isResetButtonDisabled={isResetButtonDisabled}
                    setResetButtonStatus={setColumnSelectorTouched}
                    withoutHighlight={true}
                    classNames='aligned-right'
                    key={ID_FOR_COLUMN_SELECTOR}
                    isSetInitialItems={true}
                />
            )
        },
        {
            customNode:
                <div className={classNames('search-input-container', {
                    'no-result': (
                        !vm.getAttendees()?.length &&
                        !!vm.searchQuery
                    )
                })}>
                    <SearchInput
                        resetSearch={resetSearchQuery}
                        onChange={(e, value) => vm.setSearchQuery(value)}
                        withResetSearchButton
                        withDebounce
                    />
                </div>,
            width: '145px'
        }
    ];

    // TODO uncomment current code if you want to use upload Attendees functionality. Pined ticket SLEIP-478

    const uploadAttendeesButtonProperties = vm.composeUploadAttendeesButtonProperties();
    const generateAccessCodesButtonProperties = vm.composeGenerateAccessCodesButtonProperties();
    const uploadAccessCodesButtonProperties = vm.composeUploadAccessCodesButtonProperties();
    const generateOrganizationsLinksButtonProperties = vm && vm.composeGenerateOrganizationsLinksButtonProperties();
    const attendeesOptions = [
        hasPermission(Permission.CREATE_ORGANIZATION) && {
            label: intl.formatMessage({
                id: 'oe.organization.new',
                defaultMessage: '{prefix}New Organization',
                description: 'Label text'
            }, {
                prefix: ''
            }),
            action: vm.showCreateOrganization
        },
        hasPermission(Permission.ATTENDEE_DOWNLOAD) && {
            label: intl.formatMessage({
                id: 'oe.attendees.downloadCSV',
                defaultMessage: 'Download Attendees CSV',
                description: 'Label text'
            }),
            action: vm.downloadAttendeesCSV,
            disabled: vm.isAttendeeListEmpty()
        },
        hasPermission(Permission.PRESENTER_LINK_DOWNLOAD) && {
            label: intl.formatMessage({
                id: 'oe.organization.downloadPresenterOrganizationLink',
                defaultMessage: 'Download Presenter Organization Links',
                description: 'Label text'
            }),
            action: vm.downloadOrganizationsLinksCSV
        },
        hasPermission(Permission.ATTENDEE_UPLOAD) && {
            label: uploadAttendeesButtonProperties.text,
            disabled: uploadAttendeesButtonProperties.disabled,
            action: () => vm.setUploadCSVModalOpen(true)
        },
        hasPermission(Permission.ACCESS_CODES_MANAGEMENT) && {
            label: uploadAccessCodesButtonProperties.text,
            disabled: uploadAccessCodesButtonProperties.disabled,
            action: () => vm.setUploadAccessCodesCSVModalOpen(true)
        },
        hasPermission(Permission.ACCESS_CODES_MANAGEMENT) && {
            label: generateAccessCodesButtonProperties.text,
            disabled: generateAccessCodesButtonProperties.disabled,
            action: vm.generateAccessCodes
        },
        hasPermission(Permission.BULK_GENERATE_ORGANIZATIONS_LINKS) && {
            label: generateOrganizationsLinksButtonProperties.text,
            disabled: generateOrganizationsLinksButtonProperties.disabled,
            action: vm.generateOrganizationsLinks
        }
    ].filter(Boolean);

    useEffect(() => {
        vm.clearFilters();
        vm.fetchCustomFields();
        if (!storedAttendeeColumnList && storedAttendeeColumnList !== '') {
            localStorage.setItem(showedAttendeeColumnList, list.filter(el => el.byDefault).map(el => el.id).join());
            setColumnFilter(list.filter(el => el.byDefault).map(el => el.id).join());
        }

        return () => {
            vm.clearAttendeeSelectedIds();
        };
    }, []);

    return (
        <div>
            <section className='horizontal-row horizontal-row--with-bottom-margin event-tabs'>
                <ManageEventNavigation />
                <div className='horizontal-row__align-to-row-end'>
                    <div className='buttons-block'>
                        {hasPermission(Permission.CREATE_ATTENDEE) &&
                            <DefaultButton
                                modifier={['fat', 'confirm-safe']}
                                text={intl.formatMessage({
                                    id: 'oe.attendee.new',
                                    defaultMessage: '{prefix}New Attendee',
                                    description: 'Label text'
                                }, {
                                    prefix: '+ '
                                })}
                                onClick={() => vm.addAttendee(OpenFrom.Attendees)}
                            />
                        }
                        {hasPermission(Permission.UPDATE_ATTENDEE) && !!vm.attendeeSelectedIds.length &&
                            <DefaultButton
                                modifier={['fat', 'confirm-safe']}
                                text={intl.formatMessage({
                                    id: 'oe.attendee.bulkEdit',
                                    defaultMessage: 'Edit {prefix} Attendees',
                                    description: 'Label text'
                                }, {
                                    prefix: vm.attendeeSelectedIds.length
                                })}
                                onClick={() => vm.bulkEditAttendees(vm.attendeeSelectedIds)}
                            />
                        }
                        {!!attendeesOptions.length && <OptionsButton
                            modifier={['fat', 'options']}
                            label={intl.formatMessage(MORE_BUTTON_TEXT)}
                            options={attendeesOptions}
                        />}
                    </div>
                </div>
            </section>
            <section className='attendees-list fixed-list-layout'>
                <div>
                    <ListLayout
                        sortOptions={{
                            theads: theads.filter(Boolean),
                            sortRows: vm.setSortParams,
                            sortOrder: vm.getSortOrder(),
                            sortField: vm.getSortField()
                        }}
                        isDisabled={isEmpty}
                    >
                        {(!vm.isFetchingAttendees() && vm.getAttendees())
                            ? vm.getAttendees().map((item: IAttendeeListItemDTO) => {
                                return <AttendeesListItem
                                    accountId={vm.accountId}
                                    eventId={vm.eventId}
                                    onEdit={() => vm.editAttendee(item.attendeeId, OpenFrom.Attendees)}
                                    isEditable={isEditable}
                                    attendee={item}
                                    isSelected={vm.isAttendeeEditFormShown(item.attendeeId)}
                                    key={item.attendeeId}
                                    searchQuery={vm.searchQuery}
                                    customFields={vm.customFields}
                                    handleChangeAttendeeSelection={() => vm.handleAttendeeSelect(item.attendeeId, isAttendeeSelected(item.attendeeId))}
                                    isAttendeeSelected={isAttendeeSelected(item.attendeeId)}
                                    attendeeDetailsPopoverVM={vm.attendeDetailsVM}
                                />;
                            })
                            : <TableLoadingRow colSpan={theads.length} />
                        }
                    </ListLayout>
                    {isEmpty &&
                        <div className='empty-list-message'>
                            {!vm.searchQuery ?
                                <FormattedMessage
                                    description='Attendees list empty text'
                                    defaultMessage='No attendees have been added to this event.'
                                    id='oe.attendees.emptyListText'
                                />
                                :
                                <FormattedMessage {...EMPTY_SEARCH_RESULT} />
                            }
                        </div>
                    }
                </div>
                {!isLoading && <Paging pagingVM={vm.getPagingVM()} />}
                <AttendeeUploadPopover
                    titlePopover={
                        intl.formatMessage({
                            id: 'oe.attendees.upload',
                            defaultMessage: 'Upload Attendees',
                            description: 'Label text'
                        })
                    }
                    popoverIsOpen={vm.isUploadCSVModalOpen}
                    closePopover={() => vm.setUploadCSVModalOpen(false)}
                    defaultButtonText={intl.formatMessage({
                        id: 'oe.attendees.uploadCSV',
                        defaultMessage: 'Upload Attendees CSV',
                        description: 'Label text'
                    })
                    }
                    importCSV={vm.importAttendeesCSV}
                    defaultButtonDisabled={!vm.attendeeCSVFile}
                    uploadCSV={vm.setAttendeeCSVFile}
                    nameCSV={vm.attendeeCSVFile ? vm.attendeeCSVFile.name : ''}

                />

                <AttendeeUploadPopover
                    titlePopover={intl.formatMessage({
                        id: 'oe.attendees.uploadAccessCodes',
                        defaultMessage: 'Upload Access Codes',
                        description: 'Label text'
                    })}
                    popoverIsOpen={vm.isUploadAccessCodesCSVModalOpen}
                    closePopover={() => vm.setUploadAccessCodesCSVModalOpen(false)}
                    defaultButtonText={intl.formatMessage({
                        id: 'oe.attendees.uploadAccessCodesCSV',
                        defaultMessage: 'Upload Access Codes CSV',
                        description: 'Label text'
                    })}

                    importCSV={vm.importAccessCodesCSV}
                    defaultButtonDisabled={!vm.accessCodesCSVFile}
                    uploadCSV={vm.setAccessCodesCSVFile}
                    nameCSV={vm.accessCodesCSVFile ? vm.accessCodesCSVFile.name : ''}
                />
            </section>
        </div>
    );
}));

export const AttendeesListContainer = (props: { vm: AttendeeListViewModel }) => {
    const { vm } = props;

    useEffect(() => {
        vm.attendeDetailsVM.setClose();
        vm.setSearchQuery('');
        vm.fetchAttendees();
        vm.clearFilters();

        return () => {
            vm.clearAttendees();
        };
    }, []);

    return <AttendeesListPresentation
        vm={vm}
        resetSearchQuery={vm.clearSearchQuery}
    />;
};

export { AttendeesListContainer as AttendeesList, AttendeesListPresentation };
